import React, { createContext, useCallback, useContext, useEffect, useState, useMemo } from 'react';
import { getBetfairStatus, betfairErrorMessage, BetfairStatus, Side } from '../api/betfair';

// One line in the bet slip — a BACK or LAY order on a Betfair exchange runner
// (the only real placement path the backend exposes). A runner can appear twice
// (once backed, once laid), so `side` is part of the slip id.
export interface SlipItem {
  id: string; // `${marketId}:${selectionId}:${side}`
  marketId: string;
  selectionId: number;
  runnerName: string;
  side: Side;
  price: number;
  stake: number;
  meetingName?: string;
  raceNumber?: number;
  date?: string;
  marketName?: string;
  persistenceType?: 'LAPSE' | 'MARKET_ON_CLOSE';
  winMarketId?: string;
  marketType?: 'WIN' | 'PLACE';
  racingCode?: 'HORSE' | 'GREYHOUND' | 'SPORTS';
  number?: number;
  cardKey?: string; // set when the line belongs to a runner card (Bucket Next Up click)
}

export type AddSelection = Omit<SlipItem, 'id' | 'stake'> & { stake?: number };

export interface MultipleSelection {
  win_market_id: string;
  selection_id: number;
  runner: string;
  number?: number;
  track?: string;
  race?: number;
  type: 'WIN' | 'PLACE' | 'EW';
  side?: Side;
  racing_code?: 'HORSE' | 'GREYHOUND' | 'SPORTS';
  prices?: Partial<Record<'WIN'|'PLACE',number>>;
}

// Owner 2026-10-13: a horse clicked on Bucket Next Up becomes a runner card in the slip.
export interface RunnerCard { key: string; track: string; race: number; date: string; runner: string; }

interface BetSlipContextValue {
  cards: RunnerCard[];
  addCard: (c: Omit<RunnerCard, 'key'>) => void;
  removeCard: (key: string) => void;
  items: SlipItem[];
  status: BetfairStatus | null;
  statusError: string | null;
  open: boolean;
  minStake: number;
  addItem: (sel: AddSelection) => void;
  removeItem: (id: string) => void;
  updateItem: (id: string, patch: Partial<Pick<SlipItem, 'price' | 'stake'>>) => void;
  hasItem: (id: string) => boolean;
  clear: () => void;
  setOpen: (v: boolean) => void;
  refreshStatus: () => Promise<void>;
  product: 'singles' | 'multiples';
  setProduct: (v: 'singles' | 'multiples') => void;
  multipleItems: MultipleSelection[];
  addMultiple: (item: MultipleSelection) => void;
  removeMultiple: (market: string, selection: number, side?: Side) => void;
  clearMultiples: () => void;
}

const BetSlipContext = createContext<BetSlipContextValue | null>(null);

export const slipId = (marketId: string, selectionId: number, side: Side) =>
  `${marketId}:${selectionId}:${side}`;

export const BetSlipProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<SlipItem[]>([]);
  const [status, setStatus] = useState<BetfairStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [product,setProduct]=useState<'singles'|'multiples'>('multiples');
  const [manualMultiples,setMultipleItems]=useState<MultipleSelection[]>([]);
  const [cards,setCards]=useState<RunnerCard[]>([]);
  const addCard=useCallback((c:Omit<RunnerCard,'key'>)=>{
    const key=`${c.date}|${c.track}|${c.race}|${c.runner}`.toUpperCase();
    setCards(prev=>prev.some(x=>x.key===key)?prev:[...prev,{...c,key}]);setOpen(true);
  },[]);
  const removeCard=useCallback((key:string)=>{setCards(prev=>prev.filter(x=>x.key!==key));setItems(prev=>prev.filter(i=>i.cardKey!==key));},[]);
  const addMultiple=useCallback((item:MultipleSelection)=>{
    setMultipleItems(prev=>{
      const found=prev.find(x=>x.win_market_id===item.win_market_id&&x.selection_id===item.selection_id&&(x.side||'BACK')===(item.side||'BACK'));
      if(found?.type===item.type)return prev.filter(x=>x!==found);
      return found?prev.map(x=>x===found?item:x):[...prev,item];
    });setProduct('multiples');setOpen(true);
  },[]);
  const removeMultiple=useCallback((market:string,selection:number,side:Side='BACK')=>{
    setMultipleItems(prev=>prev.filter(x=>x.win_market_id!==market||x.selection_id!==selection||(x.side||'BACK')!==side));
    setItems(prev=>prev.filter(x=>x.side!==side||x.winMarketId!==market||x.selectionId!==selection));
  },[]);
  const clearMultiples=useCallback(()=>{setMultipleItems([]);setItems(prev=>prev.filter(x=>!x.winMarketId));},[]);
  const multipleItems = useMemo(() => {
    const merged = new Map<string, MultipleSelection>();
    for (const it of items) {
      if (!it.winMarketId || !it.marketType) continue;
      const key = `${it.winMarketId}:${it.selectionId}:${it.side}`;
      const prior = merged.get(key);
      merged.set(key, {win_market_id:it.winMarketId,selection_id:it.selectionId,runner:it.runnerName,
        number:it.number,track:it.meetingName,race:it.raceNumber,side:it.side,
        racing_code:it.racingCode||'HORSE',
        prices:{...prior?.prices,[it.marketType]:it.price},
        type:prior && prior.type !== it.marketType ? 'EW' : it.marketType});
    }
    for (const item of manualMultiples) merged.set(`${item.win_market_id}:${item.selection_id}:${item.side||'BACK'}`,item);
    return Array.from(merged.values());
  },[items,manualMultiples]);

  const minStake = status?.minStake ?? 5;

  const refreshStatus = useCallback(async () => {
    try {
      const s = await getBetfairStatus();
      setStatus(s);
      setStatusError(null);
    } catch (err) {
      setStatus(null);
      setStatusError(betfairErrorMessage(err));
    }
  }, []);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const addItem = useCallback(
    (sel: AddSelection) => {
      const id = slipId(sel.marketId, sel.selectionId, sel.side);
      if(sel.winMarketId) setMultipleItems(prev=>prev.filter(x=>x.win_market_id!==sel.winMarketId || x.selection_id!==sel.selectionId || (x.side||'BACK')!==sel.side));
      setItems((prev) => {
        // Clicking the same runner+market again toggles it back out of the slip.
        if (prev.some((it) => it.id === id)) {
          return prev.filter((it) => it.id !== id);
        }
        const next: SlipItem = {
          ...sel,
          id,
          stake: sel.stake ?? (status?.minStake ?? 5),
        };
        return [next, ...prev];
      });
      setOpen(true);
      setProduct('singles');
    },
    [status]
  );

  const removeItem = useCallback((id: string) => {
    const removed=items.find(it=>it.id===id);
    if(removed?.winMarketId) setMultipleItems(prev=>prev.filter(x=>x.win_market_id!==removed.winMarketId || x.selection_id!==removed.selectionId || (x.side||'BACK')!==removed.side));
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, [items]);

  const updateItem = useCallback(
    (id: string, patch: Partial<Pick<SlipItem, 'price' | 'stake'>>) => {
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
    },
    []
  );

  const hasItem = useCallback((id: string) => items.some((it) => it.id === id), [items]);

  const clear = useCallback(() => { setItems([]); setCards([]); }, []);

  const value: BetSlipContextValue = {
    items,
    status,
    statusError,
    open,
    minStake,
    addItem,
    removeItem,
    updateItem,
    hasItem,
    clear,
    setOpen,
    refreshStatus,
    product,setProduct,multipleItems,addMultiple,removeMultiple,clearMultiples,
    cards,addCard,removeCard,
  };

  return <BetSlipContext.Provider value={value}>{children}</BetSlipContext.Provider>;
};

export const useBetSlip = (): BetSlipContextValue => {
  const ctx = useContext(BetSlipContext);
  if (!ctx) throw new Error('useBetSlip must be used within a BetSlipProvider');
  return ctx;
};

